import {
  AfterViewInit,
  Component,
  Inject,
  Input,
  ViewChild,
  ViewContainerRef,
  OnDestroy,
} from '@angular/core';
import { Route } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/route.interface';
import { BaseMap } from '../../../../shared/map/base-map.component';
import { MapComponent } from '../../../../shared/map/map.component';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { Overlay } from '@angular/cdk/overlay';
import {
  MapService,
  MarkersClustering,
} from 'src/app/modules/route-planner/services/map/map.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { eMapLocationType } from 'src/app/modules/route-planner/enums/route-planning/location-type.enum';
import { Stop } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/stop.interface';
import { MAP_ICONS } from 'src/app/modules/route-planner/constants/map-icons.constants';
import { StopFullData } from 'src/app/modules/route-planner/interfaces/route-planning/service/@schedule-service.interface';
import { StationsAutoCompleteMapMarkerComponent } from '../../../../route-planner-form/forms/search-station-form/stations-auto-complete/stations-auto-complete-map/stations-auto-complete-map-marker/stations-auto-complete-map-marker.component';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import { MapLocation } from 'src/app/modules/route-planner/interfaces/location/map-locations.interface';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { ScheduleRepository } from 'src/app/modules/route-planner/repositories/schedule.repository';
import { interval, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Coordinate } from 'src/app/modules/route-planner/interfaces/location/coordinate.interface';
import { zIndex } from 'html2canvas/dist/types/css/property-descriptors/z-index';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';

@Component({
    selector: 'app-search-line-result-map',
    templateUrl: './search-line-result-map.component.html',
    styleUrls: ['./search-line-result-map.component.scss'],
    providers: [MapService],
    standalone: false
})
export class SearchLineResultMapComponent
  extends BaseMap<Route>
  implements AfterViewInit, OnDestroy {
  @ViewChild(MapComponent)
  override mapComponent?: MapComponent;

  @Input() line: Route;

  activeStationMarker: H.map.Marker;
  stationDetailsMarker: H.map.DomMarker;
  private routeLocationInterval: Subscription;
  private routeLocationMarkers: H.map.Marker[] = [];
  private routePolylineObjects: H.map.Object[] = [];
  private searchLineMapDataSub: Subscription;
  private boardingStation: Stop;
  private dropingStation: Stop;

  constructor(
    favoritesService: FavoritesService,
    resizeService: ResizeService,
    overlay: Overlay,
    mapService: MapService,
    routePlanningService: RoutePlanningService,
    private viewContainerRef: ViewContainerRef,
    private scheduleRepository: ScheduleRepository,
    private readonly queryParamsService: QueryParamsService,
    @Inject(DATA_INJECTION_TOKEN)
    private readonly data: {
      line: Route;
      boardingStation: Stop;
      dropingStation: Stop;
    }
  ) {
    super(
      favoritesService,
      resizeService,
      overlay,
      mapService,
      routePlanningService
    );
    this.mapService.setViewContainerRef(this.viewContainerRef);
    this.routePlanningService.setMapService(this.mapService);
  }

  override ngAfterViewInit() {
    if (!!this.data?.line) this.line = this.data.line;
    if (!!this.data?.boardingStation) this.boardingStation = this.data.boardingStation;
    if (!!this.data?.dropingStation) this.dropingStation = this.data.dropingStation;
  

    const stopsData = this.line.stops.map((stop) => ({
      data: stop,
      coordinates: { lat: stop.lat, lng: stop.lng },
      type: eMapLocationType.STATION,
    }));


    this._mapInitialized = this.mapService.initializeMap(
      this.mapComponent.mapDiv,
      {
        markersClustering: this.getMarkersClustering(stopsData),
        //progressiveLoading: true,
        // enableCaching: true,
        controls: {
          zoom: true,
          myLocation: true,
          scalebar: true,
        },
      }
    );

    this.mapService.$mapInitialized.subscribe(() => {
      this._mapInitialized = true;

      if (this.line?.polyline) {
        this.drawRoutePolylines();
      }

      this.mapService.lookAtData(stopsData.map((stop) => stop.coordinates));
    });

    this.searchLineMapDataSub = this.routePlanningService.searchLineMapData$
      .pipe(filter((data) => data != null && data.line != null))
      .subscribe((data) => {
        this.line = data.line;
        this.boardingStation = data.boardingStation ?? undefined;
        this.dropingStation = data.dropingStation ?? undefined;
        if (this._mapInitialized && this.mapService.map) {
          if (this.line?.polyline) {
            this.removeRoutePolylines();
            this.drawRoutePolylines();
          }
          this.refreshStationMarkers();
        }
      });

    this.startRouteLocationInterval();
  }

  private refreshStationMarkers() {
    if (this.stationDetailsMarker) {
      this.mapService.removeObject(this.stationDetailsMarker);
      this.stationDetailsMarker = null;
    }
    this.activeStationMarker = null;

    this.mapService.removeClusteringLayers();

    const stopsData = this.line.stops.map((stop) => ({
      data: stop,
      coordinates: { lat: stop.lat, lng: stop.lng },
      type: eMapLocationType.STATION,
    }));

    this.mapService.addMarkersClustring(this.getMarkersClustering(stopsData));
    // this.mapService.lookAtData(stopsData.map((stop) => stop.coordinates));
  }

  private removeRoutePolylines() {
    if (this.routePolylineObjects.length) {
      this.mapService.removeObjects(this.routePolylineObjects);
      this.routePolylineObjects = [];
    }
  }

  private drawRoutePolylines() {
    const polyline = this.line.polyline;
    const hasStops =
      this.boardingStation?.lat != null &&
      this.boardingStation?.lng != null &&
      this.dropingStation?.lat != null &&
      this.dropingStation?.lng != null;

    if (!hasStops) {
      this.drawFullRouteInBlue(polyline);
      return;
    }

    const segments = this.getPolylineSegmentsBetweenStops(
      polyline,
      this.boardingStation.lat!,
      this.boardingStation.lng!,
      this.dropingStation.lat!,
      this.dropingStation.lng!
    );

    const greyStyle = {
      data: {} as any,
      style: {
        lineWidth: 10,
        strokeColor: '#8FA0B2',
      },
    };
    const greyStyleInner = {
      data: {} as any,
      style: {
        lineWidth: 8,
        strokeColor: '#8FA0B2',
      },
    };

    [segments.before, segments.after].forEach((lineString) => {
      if (lineString && (lineString.getLatLngAltArray?.()?.length ?? 0) >= 6) {
        this.routePolylineObjects.push(this.mapService.drawPolyline(lineString, greyStyle));
        this.routePolylineObjects.push(this.mapService.drawPolyline(lineString, greyStyleInner));
      }
    });

    if (segments.between && (segments.between.getLatLngAltArray?.()?.length ?? 0) >= 6) {
      this.routePolylineObjects.push(
        this.mapService.drawPolyline(segments.between, {
          data: {},
          style: { lineWidth: 10, strokeColor: '#0068F5' },
        })
      );
      this.routePolylineObjects.push(
        this.mapService.drawPolyline(segments.between, {
          data: {},
          style: {
            lineWidth: 8,
            strokeColor: UtilsService.lightenColor('#3386F7', 0.2),
          },
        })
      );
    }
  }

  private drawFullRouteInBlue(polyline: string) {
    const lineString = H.geo.LineString.fromFlexiblePolyline(polyline);
    this.routePolylineObjects.push(
      this.mapService.drawPolyline(lineString, {
        data: {},
        style: { lineWidth: 10, strokeColor: '#0068F5' },
      })
    );
    this.routePolylineObjects.push(
      this.mapService.drawPolyline(lineString, {
        data: {},
        style: {
          lineWidth: 8,
          strokeColor: UtilsService.lightenColor('#3386F7', 0.2),
        },
      })
    );
  }

  /**
   * Splits the full polyline into three segments: before boarding, between boarding and dropoff, after dropoff.
   */
  private getPolylineSegmentsBetweenStops(
    fullPolyline: string,
    fromLat: number,
    fromLng: number,
    toLat: number,
    toLng: number
  ): {
    before: H.geo.LineString | null;
    between: H.geo.LineString | null;
    after: H.geo.LineString | null;
  } {
    try {
      const lineString = H.geo.LineString.fromFlexiblePolyline(fullPolyline);
      const arr = lineString.getLatLngAltArray();
      if (!arr || arr.length < 6)
        return { before: null, between: null, after: null };

      const points: { lat: number; lng: number }[] = [];
      for (let i = 0; i < arr.length; i += 3) {
        points.push({ lat: arr[i], lng: arr[i + 1] });
      }

      const distFrom = (i: number) =>
        (points[i].lat - fromLat) ** 2 + (points[i].lng - fromLng) ** 2;
      const distTo = (i: number) =>
        (points[i].lat - toLat) ** 2 + (points[i].lng - toLng) ** 2;

      let fromIdx = 0;
      let toIdx = 0;
      for (let i = 1; i < points.length; i++) {
        if (distFrom(i) < distFrom(fromIdx)) fromIdx = i;
        if (distTo(i) < distTo(toIdx)) toIdx = i;
      }

      const startIdx = Math.min(fromIdx, toIdx);
      const endIdx = Math.max(fromIdx, toIdx);

      const buildLineString = (from: number, to: number) => {
        const ls = new H.geo.LineString();
        for (let i = from; i <= to; i++) {
          ls.pushLatLngAlt(points[i].lat, points[i].lng, 0);
        }
        const a = ls.getLatLngAltArray();
        return a && a.length >= 6 ? ls : null;
      };

      return {
        before: startIdx > 0 ? buildLineString(0, startIdx) : null,
        between: buildLineString(startIdx, endIdx),
        after:
          endIdx < points.length - 1
            ? buildLineString(endIdx + 1, points.length - 1)
            : null,
      };
    } catch {
      return { before: null, between: null, after: null };
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.routeLocationInterval) {
      this.routeLocationInterval.unsubscribe();
    }
    this.searchLineMapDataSub?.unsubscribe();
    this.clearRouteLocationMarkers();
  }

  private startRouteLocationInterval() {
    // Call immediately on start
    this.callGetRouteLocations();

    // Set up interval to call every 10 seconds
    this.routeLocationInterval = interval(10000).subscribe(() => {
      this.callGetRouteLocations();
    });
  }

  private callGetRouteLocations() {
    if (this.line?.routeId) {
      this.scheduleRepository.getRouteLocations(this.line.routeId).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.updateRouteLocationMarkers(response.data);
          }
        },
        error: (error) => {
          console.error('Error fetching route locations:', error);
        }
      });
    }
  }

  private clearRouteLocationMarkers() {
    // Remove all existing route location markers from the map
    this.routeLocationMarkers.forEach(marker => {
      this.mapService.removeObject(marker);
    });
    this.routeLocationMarkers = [];
  }

  private updateRouteLocationMarkers(locations: Coordinate[]) {
    // Clear existing markers first
    this.clearRouteLocationMarkers();

    // Add new markers for each location
    locations.forEach(location => {
      // Validate coordinates
      if (!location || location.lat == null || location.lng == null) {
        console.warn('Invalid location coordinates:', location);
        return;
      }

      const marker = new H.map.Marker(
        { lat: location.lat, lng: location.lng },
        {
          data: { type: 'route_location', location },
          icon: MAP_ICONS.on_map(), // Using existing icon for now
          zIndex: 1000,
        }
      );

      // Add click event listener if needed
      marker.addEventListener('tap', (ev) => {
        ev.stopPropagation();
      });

      // Add marker to map and track it
      this.mapService.addObject(marker);
      this.routeLocationMarkers.push(marker);
    });

  }

  getMarkersClustering(stopsData: MapLocation<Stop>[]): MarkersClustering {
    const options = {
      eps: 3,
      minWeight: 50,
    };

    return {
      layers: [
        {
          dataPoints: stopsData,
          dataPointObjectFactory: (dataPoint) => {
            if (!dataPoint.isCluster()) {
              // dataPoint.getData() returns the entire MapLocation object
              // which has structure: { type, coordinates, data }
              const raw = dataPoint.getData() as MapLocation<Stop>;
              const stop = raw.data as Stop;
              
              // Validate coordinates
              if (!stop || stop.lat == null || stop.lng == null) {
                console.warn('Invalid stop coordinates:', stop);
                return null;
              }

              // Use position from dataPoint (correct way for clustering)
              const position = dataPoint.getPosition();
              const stopLocation = { lat: stop.lat, lng: stop.lng };
              
              const isEdgeStop =
                stop.stopSequence ===( this.boardingStation?.stopSequence ?? 1) ||
                stop.stopSequence ===( this.dropingStation?.stopSequence ?? this.line.stops.length);
              
              // Create marker using position from dataPoint
              const marker = new H.map.Marker(position, {
                data: { ...stop, isEdgeStop: isEdgeStop },
                icon: !!isEdgeStop
                  ? MAP_ICONS.route_edges_transit()
                  : MAP_ICONS.location_circle_transit(),
              });

              marker.addEventListener('tap', (ev) => {
                ev.stopPropagation();
                
                // Get the marker from the event
                const clickedMarker = ev.target as H.map.Marker;
                if (clickedMarker) {
                  this.onMarkerClick(clickedMarker, stopLocation);
                }
              });

              return marker;
            }

            return null;
          },
          options: options,
        },
      ],
    };
  }

  private onMarkerClick(
    targetMarker: H.map.Marker,
    stopLocation: { lat: number; lng: number }
  ) {
    const markerData = targetMarker.getData();
    if (!markerData) {
      console.warn('Marker has no data');
      return;
    }

    const stop = markerData as Stop & {
      isEdgeStop: boolean;
    };

    // Validate clicked station data
    if (!stop || stop.stopcode == null) {
      console.warn('Invalid station data:', stop);
      return;
    }

    // Save isEdgeStop before creating StopFullData
    const isEdgeStop = stop.isEdgeStop || false;

    // Create StopFullData with default values for missing fields
    // Stop from route doesn't have all StopFullData fields, so we create a minimal version
    const clickedStation: StopFullData = {
      ...stop,
      routesInStop: [],
      photos: [],
      stopTimes: [],
      isAlternativeStation: false,
      rating: null,
    };

   

    const activeStation =
      this.stationDetailsMarker?.getData() as StopFullData & {
        isEdgeStop: boolean;
      };

    this.activeStationMarker?.setIcon(
      !!activeStation.isEdgeStop
        ? MAP_ICONS.route_edges_transit()
        : MAP_ICONS.location_circle_transit()
    );
    this.mapService.removeObject(this.stationDetailsMarker);

    if (activeStation?.stopcode !== clickedStation.stopcode) {
      const container = this.mapService.createMapObject<
        StationsAutoCompleteMapMarkerComponent,
        any
      >(StationsAutoCompleteMapMarkerComponent, { stop: clickedStation, showAddStopButton: false, showOriginDestinationButtons: false, showPickStationButton: true});
      const icon: H.map.DomIcon = new H.map.DomIcon(container, {
        onAttach: (element) => {
          const button = element.getElementsByClassName('choose-station').item(0);
          if (button) {
            button.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              this.pickStation(clickedStation)
            });
          } else {
            console.warn('Button not found in marker DOM');
          }

          const closeButton = element.getElementsByClassName('close-station-data').item(0);
          if (closeButton) {
            closeButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.activeStationMarker?.setIcon(
                !!isEdgeStop
                  ? MAP_ICONS.route_edges_transit()
                  : MAP_ICONS.location_circle_transit()
              );

              this.mapService.removeObject(this.stationDetailsMarker)
              this.activeStationMarker = null;
              this.stationDetailsMarker = null;
            });
          } else {
            console.warn('closeButton not found in marker DOM');
          }
        }
      });

      targetMarker.setIcon(MAP_ICONS.selected_location_circle_transit());
      this.stationDetailsMarker = this.mapService.addDomMarker(
        stopLocation,
        icon,
        clickedStation
      );
      this.mapService.updateMapView(
        clickedStation.lat,
        clickedStation.lng,
        18,
        true
      );
      this.activeStationMarker = targetMarker;
    } else {
      this.activeStationMarker = null;
      this.stationDetailsMarker = null;
    }
  }

  pickStation(station: StopFullData) {
    this.routePlanningService.closeOverlay();
    this.queryParamsService.setQueryParam('routeId', null);
    this.queryParamsService.setQueryParam('oppositeRouteId', null);
    this.queryParamsService.setQueryParam('boardingStation', null);
    this.queryParamsService.setQueryParam('dropingStation', null);
    this.queryParamsService.setQueryParam('tab', eTab.STATIONS);
    this.queryParamsService.setQueryParam('stop', station.stopcode);
    this.closeCard();
  }
}
